import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterState, IUsedDocIds } from "./interfaces/FilterState";
import { fetchData } from "./services/itemList.service";
import { SearchInput } from "./components/SearchInput/SearchInput";
import { FilterChips } from "./components/ListBlock/components/Filters/components/FilterChips/FilterChips";
import { formatDate } from "./helpers/formatDate";
import { ListBlock } from "./components/ListBlock/ListBlock";
import { ChatBlock } from "./components/ChatBlock/ChatBlock";
import { IListItem } from "./interfaces/IDocDetails";
import { INITIAL_DEFAULT_FILTER, useFilterContext } from "../../context/FilterContext";
import { updateUrlParams } from "./helpers/updateUrlParams";
import { fetchFavoriteList } from "./services/fetchFavoriteList.service";
import { useFavoriteListContext } from "../../context/FavoriteListContext";
import { useTranslation } from "react-i18next";
import { exportDataFiles } from "./services/exportDataFiles.service";
import { getExportedDocFilename } from "./helpers/getExportedDocFilename";
import { toast } from "react-toastify";
import { AddNewRecordsModal } from "../../components/Header/components/AddNewRecordsModal/AddNewRecordsModal";
import { fetchDefaultFilterData } from "../AdminPanelPage/services/defaultFilters.service";
import { useDefaultFiltersContext } from "../../context/DefaultFiltersContext";
import Select from "react-select";
import { FiDownload, FiPlusCircle } from "react-icons/fi";
import { FiFileText } from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";

const HomePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [offsetCount, setOffsetCount] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);
  const { filterState, setFilterState } = useFilterContext();
  const { setFavoriteList } = useFavoriteListContext();

  const [activeTab, setActiveTab] = useState<"all" | "favorite">(() => {
    const lsVal = localStorage.getItem("isFavorite");
    if (lsVal === "true") return "favorite";
    return location.pathname === "/favorite" ? "favorite" : "all";
  });

  const [showNewRecordsModal, setNewRecordsModal] = useState(false);
  const [searchParams] = useSearchParams();
  const [useIds, setUseIds] = useState<IUsedDocIds[]>([]);
  const ref = useRef(null);

  const documentKeysRef = useRef<Set<string>>(new Set());
  const duplicatePageCountRef = useRef(0);
  const MAX_DUPLICATE_PAGES = 10;

  const isHomePage =
    location.pathname === "/recent" || location.pathname === "/favorite";
  const darkMode = localStorage.getItem("darkMode") === "true";

  const { defaultFilters, setDefaultFilters } = useDefaultFiltersContext();
  const { user } = useUserContext();
  const loadingToastId = useRef<any>(null);
  const [mergedItems, setMergedItems] = useState<IListItem[]>([]);
  const [settingsFilter, setSettingsFilter] = useState<{
    country: string[];
    regulator: string[];
    classes: string[];
  }>({ country: [], regulator: [], classes: [] });

  const FILTER_URL_KEYS = new Set([
    "country",
    "regulator",
    "classes",
    "keywords",
    "tags",
    "tech_product",
    "tech_section_vat_act",
    "tax_alert",
    "gazette_number",
    "judges",
    "status",
    "city",
    "subject_matter",
    "date_from",
    "date_to",
    "date_issue_from",
    "date_issue_to",
    "search",
  ]);

  const country = filterState.country ?? [];
  const regulator = filterState.regulator ?? [];

  const isRomaniaOnly =
    (country.length === 1 && country[0] === "Romania" && regulator.length === 0) ||
    (country.length === 0 &&
      regulator.length === 1 &&
      regulator[0] === "Romanian Official Gazette") ||
    (country.length === 1 &&
      country[0] === "Romania" &&
      regulator.length === 1 &&
      regulator[0] === "Romanian Official Gazette");

  const actionsOptions = [
    ...(user?.roles?.length !== 0
      ? [{ value: "addNewRecord", label: t("newRecordModal.addNewRecordTitle") }]
      : []),

    ...(isRomaniaOnly
      ? [
          { value: "generateTaxAlertRo", label: "Generate Tax Alert (RO)" },
          { value: "generateTaxAlertEng", label: "Generate Tax Alert (ENG)" },
        ]
      : []),

    { value: "exportDocx", label: t("exportDocx") },
    { value: "exportXlsx", label: t("exportExcel") },
  ];

  const { data: defaultFilterData } = useQuery({
    queryKey: ["defaultFilterData"],
    queryFn: () => fetchDefaultFilterData(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (defaultFilterData) setDefaultFilters(defaultFilterData);
  }, [defaultFilterData, setDefaultFilters]);

  const filtersToSearch = (f: FilterState) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) {
      if (Array.isArray(v)) v.forEach((x) => x && p.append(k, x));
      else if (v instanceof Date) p.set(k, v.toISOString());
      else if (v) p.set(k, String(v));
    }
    return p.toString();
  };

  const readSettingsFromLS = () => {
    try {
      const raw = localStorage.getItem("settings");
      if (!raw) return { country: [], regulator: [], classes: [] };
      const parsed = JSON.parse(raw);
      return {
        country: Array.isArray(parsed?.settings?.country)
          ? parsed.settings.country
          : Array.isArray(parsed?.country)
            ? parsed.country
            : [],
        regulator: Array.isArray(parsed?.settings?.regulator)
          ? parsed.settings.regulator
          : Array.isArray(parsed?.regulator)
            ? parsed.regulator
            : [],
        classes: Array.isArray(parsed?.settings?.classes)
          ? parsed.settings.classes
          : Array.isArray(parsed?.classes)
            ? parsed.classes
            : [],
      };
    } catch {
      return { country: [], regulator: [], classes: [] };
    }
  };

  useEffect(() => {
    const handleSettingsUpdated = () => {
      setSettingsFilter(readSettingsFromLS());
    };

    handleSettingsUpdated();
    window.addEventListener("settings:updated", handleSettingsUpdated as EventListener);
    window.addEventListener("storage", handleSettingsUpdated);
    return () => {
      window.removeEventListener("settings:updated", handleSettingsUpdated as EventListener);
      window.removeEventListener("storage", handleSettingsUpdated);
    };
  }, []);

  const normalizeCountryKey = (countryValue: string): string | null => {
    const value = countryValue.toLowerCase();
    if (value.includes("roman")) return "romania";
    if (value.includes("hungar")) return "hungary";
    if (value.includes("europe") || value.includes("eu")) return "europe";
    if (value.includes("czech")) return "czechrepublic";

    const sanitized = value.replace(/\s+/g, "").trim();
    return sanitized || null;
  };

  const applySettingsToFilters = (filters: FilterState): FilterState => {
    const next: FilterState = { ...filters };

    if (!filters.country?.length && settingsFilter.country.length > 0) {
      next.country = settingsFilter.country;
    }

    if (!filters.regulator?.length) {
      if (settingsFilter.regulator.length > 0) {
        next.regulator = settingsFilter.regulator;
      }
    }

    if (!filters.classes?.length && settingsFilter.classes.length > 0) {
      next.classes = settingsFilter.classes;
    }

    return next;
  };

  const settingsFilterKey = useMemo(
    () => JSON.stringify(settingsFilter),
    [settingsFilter],
  );

  const effectiveFilters = useMemo(
    () => applySettingsToFilters(filterState),
    [filterState, settingsFilterKey],
  );

  const filterKey = useMemo(
    () => filtersToSearch(effectiveFilters),
    [effectiveFilters],
  );

  const useIdsKey = useMemo(
    () => JSON.stringify(useIds ?? []),
    [useIds],
  );

  const { data, isFetching, refetch } = useQuery({
    placeholderData: (prev) => prev,
    queryKey: ["data", offsetCount, filterKey, activeTab, useIdsKey],
    queryFn: () =>
      fetchData(
        offsetCount,
        {
          ...effectiveFilters,
          date_from: formatDate(effectiveFilters.date_from),
          date_to: formatDate(effectiveFilters.date_to),
          date_issue_from: formatDate(effectiveFilters.date_issue_from),
          date_issue_to: formatDate(effectiveFilters.date_issue_to),
        },
        useIds,
        activeTab === "favorite",
      ),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    const handler = () => {
      setOffsetCount(0);
      setMergedItems([]);
      documentKeysRef.current = new Set();
      duplicatePageCountRef.current = 0;
      setIsDisabled(false);

      try {
        queryClient.invalidateQueries({ queryKey: ["data"], exact: false });
        refetch?.();
      } catch (e) {}
    };

    window.addEventListener("records:created", handler as EventListener);
    return () => window.removeEventListener("records:created", handler as EventListener);
  }, [refetch, queryClient]);

  const { data: favoriteListData } = useQuery({
    queryKey: ["favoriteListData"],
    queryFn: () => fetchFavoriteList(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { mutate: generateReport } = useMutation({
    mutationFn: async (format: string) => {
      loadingToastId.current = toast.loading(t("exportLoading"), {
        position: "bottom-right",
      });

      const idsToSend =
        useIds && useIds.length > 0 ? useIds : (data?.use_ids ?? []);
      return await exportDataFiles(idsToSend, format, activeTab === "favorite");
    },
    onSuccess: (response, format: string) => {
      toast.dismiss(loadingToastId.current);
      toast.success(t("exportSuccess"), { position: "top-right" });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${getExportedDocFilename(format)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onError: () => {
      toast.dismiss(loadingToastId.current);
      toast.error(t("errorExport"), { position: "top-right" });
    },
  });

  useEffect(() => {
    if (favoriteListData) setFavoriteList(favoriteListData);
  }, [favoriteListData, setFavoriteList]);

  const getFilterSearchParams = (filters: any) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) value.forEach((v) => v && params.append(key, v));
      else if (value instanceof Date) params.set(key, value.toISOString());
      else if (value) params.set(key, value.toString());
    });
    return params.toString();
  };

  const resetPaginationState = () => {
    setOffsetCount(0);
    setUseIds([]);
    setMergedItems([]);
    documentKeysRef.current = new Set();
    duplicatePageCountRef.current = 0;
    setIsDisabled(false);
  };

  const handleValueSelected = (selectedValue: string) => {
    const newFilterState = { ...filterState, search: selectedValue };
    setFilterState(newFilterState);
    updateUrlParams(newFilterState);
    resetPaginationState();
  };

  const handleFiltersChange = (updatedFilters: FilterState) => {
    updateUrlParams(updatedFilters);
    setFilterState(updatedFilters);
    resetPaginationState();
  };

  const clearSearch = () => {
    const clearedFilters = { ...INITIAL_DEFAULT_FILTER, search: "" };
    setFilterState(clearedFilters);
    updateUrlParams(clearedFilters);
    resetPaginationState();
  };

  const exportFiles = (formatType: string) => generateReport(formatType);

  const handleTabClick = (tab: "all" | "favorite") => {
    localStorage.setItem("isFavorite", tab === "favorite" ? "true" : "false");
    setActiveTab(tab);
    resetPaginationState();

    if (location.pathname !== "/chatAI") {
      navigate({
        pathname: tab === "all" ? "/recent" : "/favorite",
        search: getFilterSearchParams(filterState),
      });
    }
  };

  useEffect(() => {
    setOffsetCount(0);
    setMergedItems([]);
    documentKeysRef.current = new Set();
    duplicatePageCountRef.current = 0;
    setIsDisabled(false);
  }, [filterKey, activeTab]);

  const getDocumentKey = (item: any): string => {
    const explicitDocumentId =
      item.document_id ??
      item.doc_id ??
      item.regulation_id ??
      item.parent_id ??
      item.original_id;

    if (explicitDocumentId) {
      return String(explicitDocumentId);
    }

    const title = item.title ?? item.document_title ?? "";
    const regulator = item.regulator ?? "";
    const country = item.country ?? "";
    const date = item.date ?? "";
    const dateIssue = item.date_issue ?? "";
    const status = item.status ?? "";

    const compositeKey = [
      title,
      regulator,
      country,
      date,
      dateIssue,
      status,
    ]
      .map((value) => String(value).trim().toLowerCase())
      .filter(Boolean)
      .join("|");

    if (compositeKey) {
      return compositeKey;
    }

    return String(item.id);
  };

  const dedupeByDocument = (items: IListItem[]): IListItem[] => {
    const map = new Map<string, IListItem>();

    for (const item of items as any[]) {
      const key = getDocumentKey(item);

      if (!map.has(key)) {
        map.set(key, item);
        continue;
      }

      const existing: any = map.get(key);
      const current: any = item;

      const chunkIds = Array.from(
        new Set(
          [
            ...(existing.chunk_ids ?? []),
            ...(current.chunk_ids ?? []),
            current.chunk_id,
            current.id,
          ].filter(Boolean),
        ),
      );

      map.set(key, {
        ...existing,
        ...current,
        id: existing.id ?? current.id,
        chunk_ids: chunkIds,
      });
    }

    return Array.from(map.values());
  };

  useEffect(() => {
    const rawPage = (data?.items ?? []) as IListItem[];
    const page = dedupeByDocument(rawPage);

    if (offsetCount === 0) {
      const nextKeys = new Set<string>();

      page.forEach((item: any) => {
        nextKeys.add(getDocumentKey(item));
      });

      documentKeysRef.current = nextKeys;
      duplicatePageCountRef.current = 0;

      setMergedItems(page);
      setIsDisabled(rawPage.length === 0);

      return;
    }

    let addedNewDocument = false;

    page.forEach((item: any) => {
      const key = getDocumentKey(item);

      if (!documentKeysRef.current.has(key)) {
        documentKeysRef.current.add(key);
        addedNewDocument = true;
      }
    });

    if (rawPage.length === 0) {
      setIsDisabled(true);
    } else if (addedNewDocument) {
      duplicatePageCountRef.current = 0;
      setIsDisabled(false);
    } else {
      duplicatePageCountRef.current += 1;
      setIsDisabled(duplicatePageCountRef.current >= MAX_DUPLICATE_PAGES);
    }

    setMergedItems((prev) => dedupeByDocument([...prev, ...page]));
  }, [data?.items, offsetCount]);

  const actionsSelectStyles: any = {
    control: (base: any) => ({
      ...base,
      minHeight: 40,
      height: 40,
      borderRadius: 8,
      boxShadow: "none",
      cursor: "pointer",
      backgroundColor: darkMode ? "#0f172a" : "#ffffff",
      borderColor: darkMode ? "#475569" : "#cbd5e1",
      fontSize: "14px",
      fontWeight: 600,
      ":hover": { borderColor: darkMode ? "#64748b" : "#94a3b8" },
    }),
    valueContainer: (base: any) => ({ ...base, padding: "0 8px" }),
    placeholder: (base: any) => ({
      ...base,
      color: darkMode ? "#e2e8f0" : "#0f172a",
      fontWeight: 600,
      margin: 0,
      fontSize: "14px",
    }),
    singleValue: (base: any) => ({
      ...base,
      color: darkMode ? "#e2e8f0" : "#0f172a",
      fontWeight: 600,
      margin: 0,
      fontSize: "14px",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: darkMode ? "#e2e8f0" : "#0f172a",
      padding: "0 8px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    container: (base: any) => ({ ...base, minWidth: 200 }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    menu: (base: any) => ({
      ...base,
      borderRadius: 10,
      overflow: "hidden",
      border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
      boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
      backgroundColor: darkMode ? "#0f172a" : "#ffffff",
    }),
    option: (base: any, { isFocused }: any) => ({
      ...base,
      backgroundColor: darkMode
        ? isFocused
          ? "#1f2937"
          : "#0f172a"
        : isFocused
        ? "#86BC25"
        : "#ffffff",
      color: darkMode ? "#ffffff" : "#0f172a",
      fontWeight: 400,
      fontSize: "14px",
      cursor: "pointer",
      padding: "10px 12px",
    }),
  };

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col overflow-hidden">
      {isFetching && <div className="line-loader"></div>}

      <div className="flex flex-col flex-1 overflow-hidden bg-[#F3F6F9] dark:bg-slate-800/40">
        <div>
          <div className="flex items-center justify-between mb-3 gap-3">
            <div className="flex items-center flex-1 min-w-0 gap-3">
              <SearchInput
                value={filterState.search || ""}
                onInputChange={handleValueSelected}
                onClear={() => handleFiltersChange({ ...filterState, search: "" })}
                searchType={filterState["search_field"] || "*"}
                onSearchTypeChange={(searchType) =>
                  handleFiltersChange({ ...filterState, search_field: searchType })
                }
              />

              <div className="shrink-0">
                <div className="inline-flex items-center rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => handleTabClick("all")}
                    className={[
                      "h-8 px-2 rounded-md text-sm font-medium transition",
                      activeTab === "all"
                        ? "bg-del-green text-white shadow-sm"
                        : "text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white",
                    ].join(" ")}
                  >
                    {t("tabs.all")}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTabClick("favorite")}
                    className={[
                      "h-8 px-2 rounded-md text-sm font-medium transition",
                      activeTab === "favorite"
                        ? "bg-del-green text-white shadow-sm"
                        : "text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white",
                    ].join(" ")}
                  >
                    {t("tabs.favourite")}
                  </button>
                </div>
              </div>
            </div>

            {isHomePage && (
              <div className="relative" ref={ref}>
                <Select
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  menuPortalTarget={document.body}
                  value={null}
                  placeholder={t("actions")}
                  options={actionsOptions}
                  formatOptionLabel={(option: any) => {
                    const map: Record<string, JSX.Element> = {
                      addNewRecord: <FiPlusCircle className="h-4 w-4 mr-2" />,
                      exportDocx: <FiDownload className="h-4 w-4 mr-2" />,
                      exportXlsx: <FiDownload className="h-4 w-4 mr-2" />,
                      generateTaxAlertRo: <FiFileText className="h-4 w-4 mr-2" />,
                      generateTaxAlertEng: <FiFileText className="h-4 w-4 mr-2" />,
                    };
                    return (
                      <div className="flex items-center">
                        {map[option.value] ?? null}
                        <span>{option.label}</span>
                      </div>
                    );
                  }}
                  onChange={(opt: any) => {
                    if (!opt) return;
                    if (opt.value === "addNewRecord") setNewRecordsModal(true);
                    if (opt.value === "exportDocx") exportFiles("docx");
                    if (opt.value === "exportXlsx") exportFiles("xlsx");
                    if (opt.value === "generateTaxAlertRo") exportFiles("tax_alert_ro");
                    if (opt.value === "generateTaxAlertEng") exportFiles("tax_alert_en");
                  }}
                  classNamePrefix="react-select"
                  styles={actionsSelectStyles}
                />
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <FilterChips />
          </div>
        </div>

        <div
          className={`${
            location.pathname === "/recent" || location.pathname === "/favorite" ? "" : "hidden"
          } flex flex-row space-x-4 mt-2 flex-1 min-h-0`}
        >
          <ListBlock
            listData={{
              items: mergedItems,
              filters: data?.filters ?? INITIAL_DEFAULT_FILTER,
            }}
            data={data}
            isLoading={isFetching}
            offsetCount={offsetCount}
            onFilterChange={(updatedFilters: FilterState) => handleFiltersChange(updatedFilters)}
            onOffsetChange={(nextOffset: number) => setOffsetCount(nextOffset)}
            isDisabled={isDisabled}
            onClearSearch={clearSearch}
            onItemTagsUpdate={(itemId: string, tags: string[]) => {
              setMergedItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, tags } : it)));
            }}
          />
        </div>

        <div
          className={`${
            location.pathname === "/chatAI" ? "" : "hidden"
          } flex flex-row space-x-4 mt-2 flex-1 min-h-0`}
        >
          <ChatBlock listItem={mergedItems} offset={offsetCount} />
        </div>
      </div>

      {showNewRecordsModal && (
        <AddNewRecordsModal setShowNewRecordsModal={setNewRecordsModal} darkMode={darkMode} />
      )}
    </div>
  );
};

export { HomePage };
export default HomePage;